type WebStorageName = "localStorage" | "sessionStorage";

const WEB_STORAGE_NAMES: WebStorageName[] = ["localStorage", "sessionStorage"];

// Reading the property itself is the check: a WebView with DOM storage
// disabled returns null, a browser that blocks site data throws.
const isWebStorageAvailable = (name: WebStorageName) => {
  try {
    return Boolean(window[name]);
  } catch {
    return false;
  }
};

const createMemoryStorage = (): Storage => {
  const items = new Map<string, string>();

  return {
    get length() {
      return items.size;
    },
    clear: () => {
      items.clear();
    },
    getItem: (key) => items.get(String(key)) ?? null,
    key: (index) => Array.from(items.keys())[index] ?? null,
    removeItem: (key) => {
      items.delete(String(key));
    },
    setItem: (key, value) => {
      items.set(String(key), String(value));
    },
  };
};

// Some Android WebViews (in-app browsers opened from ad networks) run with DOM
// storage disabled, so `window.localStorage` is null there. Our code and
// third-party scripts (Clerk's tab lock, GTM) call it unguarded, which threw
// during render and sent the whole page to the error boundary. Such browsers
// get an in-memory Storage instead: state lives until the next reload, which
// is what having no storage means anyway. A working native storage is never
// replaced, even when it is full.
export const installWebStorageFallback = () => {
  for (const name of WEB_STORAGE_NAMES) {
    if (isWebStorageAvailable(name)) {
      continue;
    }

    const descriptor = Object.getOwnPropertyDescriptor(window, name);

    if (descriptor && !descriptor.configurable) {
      continue;
    }

    Object.defineProperty(window, name, {
      configurable: true,
      value: createMemoryStorage(),
    });
  }
};
